import { useState } from 'react';

const CATEGORIES = ['Kurta', 'T-Shirt', 'Trousers', 'Dhoti'];

const SIZE_DATA = {
  Kurta: {
    headers: ['Size', 'Chest (cm)', 'Shoulder (cm)', 'Length (cm)', 'Sleeve (cm)'],
    rows: [
      ['XS', '86–91', '38–40', '96–98', '23–24'],
      ['S',  '91–96', '40–42', '98–100', '24–25'],
      ['M',  '96–101','42–44', '100–102','25–26'],
      ['L',  '101–107','44–46','102–104','26–27'],
      ['XL', '107–112','46–48','104–106','27–28'],
      ['XXL','112–117','48–50','106–108','28–29'],
    ],
  },
  'T-Shirt': {
    headers: ['Size', 'Chest (cm)', 'Shoulder (cm)', 'Length (cm)', 'Sleeve (cm)'],
    rows: [
      ['XS', '84–89', '38–40', '66–68', '18–19'],
      ['S',  '89–94', '40–42', '68–70', '19–20'],
      ['M',  '94–99', '42–44', '70–72', '20–21'],
      ['L',  '99–104','44–46', '72–74', '21–22'],
      ['XL', '104–109','46–48','74–76', '22–23'],
      ['XXL','109–114','48–50','76–78', '23–24'],
    ],
  },
  Trousers: {
    headers: ['Size', 'Waist (cm)', 'Hip (cm)', 'Inseam (cm)', 'Rise (cm)'],
    rows: [
      ['28', '71–74', '90–94', '78', '25'],
      ['30', '76–79', '94–98', '79', '26'],
      ['32', '81–84', '98–102','80', '27'],
      ['34', '86–89', '102–106','81','28'],
      ['36', '91–94', '106–110','82','29'],
      ['38', '96–99', '110–114','83','30'],
    ],
  },
  Dhoti: {
    headers: ['Size', 'Waist (cm)', 'Hip (cm)', 'Length (cm)', 'Width (cm)'],
    rows: [
      ['S',  '71–79', '88–94', '450', '110'],
      ['M',  '79–87', '94–100','450', '120'],
      ['L',  '87–95', '100–108','450','130'],
      ['XL', '95–103','108–116','450','140'],
    ],
  },
};

const HOW_TO = [
  { label: 'Chest', icon: '📐', desc: 'Measure around the fullest part of your chest, keeping the tape parallel to the floor.' },
  { label: 'Waist', icon: '📏', desc: 'Measure around your natural waistline, the narrowest part of your torso.' },
  { label: 'Shoulder', icon: '↔️', desc: 'Measure from shoulder tip to shoulder tip across the back, with the tape flat.' },
  { label: 'Inseam', icon: '↕️', desc: 'Measure from the crotch to the bottom of the ankle on the inside of the leg.' },
];

export default function SizeGuide() {
  const [cat, setCat]  = useState('Kurta');
  const [unit, setUnit]= useState('cm');

  const data = SIZE_DATA[cat];

  const convertVal = (val) => {
    if (unit === 'in') {
      return val.replace(/[\d.]+/g, (n) => (parseFloat(n) / 2.54).toFixed(1));
    }
    return val;
  };

  return (
    <div className="page-top">
      {/* Hero */}
      <div style={S.hero}>
        <div style={S.heroBg}>SIZE</div>
        <div style={S.heroContent}>
          <p className="sec-label" style={{ color: 'var(--gold)', marginBottom: '10px' }}>Find Your Perfect Fit</p>
          <h1 style={S.heroTitle}>SIZE GUIDE</h1>
          <p style={S.heroSub}>Use our size charts and measurement guide to find the perfect fit. When in doubt, we recommend sizing up.</p>
        </div>
      </div>

      {/* Measurement Guide */}
      <div style={S.measureSection}>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <p className="sec-label" style={{ marginBottom: '10px' }}>How to Measure</p>
          <h2 style={S.secTitle}>TAKE YOUR MEASUREMENTS</h2>
        </div>
        <div style={S.measureGrid}>
          {HOW_TO.map((m) => (
            <div key={m.label} style={S.measureCard}>
              <div style={S.measureIcon}>{m.icon}</div>
              <p style={S.measureLabel}>{m.label}</p>
              <p style={{ fontSize: '13px', color: '#666', lineHeight: 1.7 }}>{m.desc}</p>
            </div>
          ))}
        </div>
        <div style={S.tipBox}>
          <p style={{ fontSize: '13px', color: '#555', lineHeight: 1.7 }}>
            <strong>💡 Pro Tip:</strong> Use a flexible measuring tape and wear minimal clothing. Keep the tape snug but not tight. For garment measurements, lay the item flat and measure accordingly.
          </p>
        </div>
      </div>

      {/* Size Table */}
      <div style={S.tableSection}>
        {/* Controls */}
        <div style={S.controls}>
          <div style={S.catTabs}>
            {CATEGORIES.map((c) => (
              <button key={c}
                style={{ ...S.catTab, background: cat === c ? 'var(--black)' : '#fff', color: cat === c ? '#fff' : 'var(--black)', borderColor: cat === c ? 'var(--black)' : 'var(--border)' }}
                onClick={() => setCat(c)}>{c}</button>
            ))}
          </div>
          <div style={S.unitSwitch}>
            {['cm', 'in'].map((u) => (
              <button key={u}
                style={{ ...S.unitBtn, background: unit === u ? 'var(--red)' : '#fff', color: unit === u ? '#fff' : 'var(--black)', borderColor: unit === u ? 'var(--red)' : 'var(--border)' }}
                onClick={() => setUnit(u)}>{u.toUpperCase()}</button>
            ))}
          </div>
        </div>

        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                {data.headers.map((h) => <th key={h} style={S.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row, i) => (
                <tr key={i} style={S.tr}>
                  <td style={{ ...S.td, fontFamily: 'var(--font-display)', fontSize: '16px', letterSpacing: '1px', fontWeight: 700 }}>{row[0]}</td>
                  {row.slice(1).map((cell, j) => (
                    <td key={j} style={S.td}>{convertVal(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={S.disclaimer}>
          All measurements are approximate. Actual garment measurements may vary by ±1–2 cm due to the handcrafted nature of our products.
        </div>
      </div>

      {/* Still unsure */}
      <div style={S.helpBanner}>
        <p style={{ fontFamily: 'var(--font-display)', fontSize: '20px', letterSpacing: '2px', color: '#fff', marginBottom: '8px' }}>STILL UNSURE ABOUT YOUR SIZE?</p>
        <p style={{ fontSize: '13px', color: 'rgba(255,255,255,.5)', marginBottom: '20px' }}>Our style team is happy to help you find the perfect fit over chat or email.</p>
        <a href="mailto:support@bhadralok.com" style={S.helpCta}>EMAIL US YOUR MEASUREMENTS →</a>
      </div>
    </div>
  );
}

const S = {
  hero:          { background: 'var(--black)', padding: '60px 56px 52px', position: 'relative', overflow: 'hidden', borderBottom: '4px solid var(--gold)' },
  heroBg:        { position: 'absolute', right: '-20px', top: '50%', transform: 'translateY(-50%)', fontFamily: 'var(--font-display)', fontSize: 'clamp(80px,15vw,220px)', letterSpacing: '-4px', color: 'rgba(255,255,255,.04)', lineHeight: 1, userSelect: 'none' },
  heroContent:   { position: 'relative', zIndex: 1 },
  heroTitle:     { fontFamily: 'var(--font-display)', fontSize: 'clamp(32px,5vw,56px)', letterSpacing: '2px', color: '#fff', lineHeight: 1, marginBottom: '14px' },
  heroSub:       { fontSize: '14px', color: 'rgba(255,255,255,.5)', lineHeight: 1.8, maxWidth: '460px' },
  measureSection:{ padding: '72px 56px', background: '#fff' },
  secTitle:      { fontFamily: 'var(--font-display)', fontSize: 'clamp(22px,3vw,36px)', letterSpacing: '2px', lineHeight: 1.1 },
  measureGrid:   { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '20px', marginBottom: '28px' },
  measureCard:   { background: 'var(--offwhite)', border: '1px solid var(--border)', padding: '22px', textAlign: 'center' },
  measureIcon:   { fontSize: '28px', marginBottom: '10px' },
  measureLabel:  { fontFamily: 'var(--font-display)', fontSize: '14px', letterSpacing: '2px', marginBottom: '8px' },
  tipBox:        { background: 'var(--light)', border: '1px solid var(--border)', borderLeft: '3px solid var(--gold)', padding: '14px 18px' },
  tableSection:  { padding: '56px', background: 'var(--offwhite)' },
  controls:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  catTabs:       { display: 'flex', gap: '0' },
  catTab:        { padding: '9px 18px', border: '1.5px solid', fontFamily: 'var(--font-body)', fontSize: '11px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', marginRight: '-1px', transition: 'all .2s' },
  unitSwitch:    { display: 'flex', border: '1.5px solid var(--border)', overflow: 'hidden' },
  unitBtn:       { padding: '8px 16px', border: 'none', borderRight: '1px solid var(--border)', fontFamily: 'var(--font-body)', fontSize: '11px', fontWeight: 700, letterSpacing: '1px', cursor: 'pointer', transition: 'all .2s' },
  tableWrap:     { background: '#fff', border: '1px solid var(--border)', overflow: 'auto' },
  table:         { width: '100%', borderCollapse: 'collapse', minWidth: '500px' },
  th:            { padding: '12px 16px', fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', background: 'var(--black)', color: 'rgba(255,255,255,.7)', textAlign: 'left' },
  tr:            { borderBottom: '1px solid var(--border)', transition: 'background .15s' },
  td:            { padding: '13px 16px', fontSize: '13px', color: 'var(--black)' },
  disclaimer:    { fontSize: '11px', color: '#aaa', marginTop: '12px', fontStyle: 'italic' },
  helpBanner:    { padding: '60px 56px', background: 'var(--black)', textAlign: 'center', borderTop: '3px solid var(--red)' },
  helpCta:       { display: 'inline-block', background: 'var(--red)', color: '#fff', padding: '13px 32px', fontFamily: 'var(--font-body)', fontSize: '11px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', textDecoration: 'none' },
};
